import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { useNavigate, Link } from "react-router-dom";
import { Waves, LogOut, ShieldCheck, Store, LayoutDashboard } from "lucide-react";

export function AppShell({ children, title, subtitle, actions }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-zinc-950/80 border-b border-zinc-800/60">
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-sm bg-orange-600 flex items-center justify-center">
              <Waves className="h-5 w-5 text-white" strokeWidth={2.5} />
            </div>
            <div className="leading-tight">
              <div className="font-black tracking-tighter text-lg">LifeLike</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                License Portal
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {user?.role === "customer" && (
              <>
                <Link to="/dashboard" data-testid="nav-dashboard-link" className="hidden sm:flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-zinc-400 hover:text-orange-400 transition-colors">
                  <LayoutDashboard className="h-4 w-4" /> Dashboard
                </Link>
                <Link to="/marketplace" data-testid="nav-marketplace-link" className="hidden sm:flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-zinc-400 hover:text-orange-400 transition-colors">
                  <Store className="h-4 w-4" /> Marketplace
                </Link>
              </>
            )}
            <Link
              to="/verify"
              data-testid="nav-verify-link"
              className="hidden sm:flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-zinc-400 hover:text-orange-400 transition-colors"
            >
              <ShieldCheck className="h-4 w-4" /> Verify
            </Link>
            {user && (
              <div className="hidden md:block text-right leading-tight">
                <div className="text-sm font-medium">{user.name}</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-orange-500">
                  {user.role}
                </div>
              </div>
            )}
            <Button
              data-testid="logout-btn"
              variant="outline"
              size="sm"
              className="rounded-sm border-zinc-700 hover:bg-zinc-800 hover:text-white"
              onClick={() => {
                logout();
                navigate("/login");
              }}
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 py-10">
        {(title || actions) && (
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
            <div>
              <h1 className="text-4xl sm:text-5xl font-black tracking-tighter">{title}</h1>
              {subtitle && <p className="text-zinc-400 mt-2 max-w-2xl">{subtitle}</p>}
            </div>
            {actions}
          </div>
        )}
        {children}
      </main>
    </div>
  );
}

export function StatusBadge({ status }) {
  const map = {
    active: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
    trial: "bg-orange-500/15 text-orange-400 border-orange-500/30",
    expired: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30",
    revoked: "bg-red-500/15 text-red-400 border-red-500/30",
  };
  return (
    <span
      data-testid={`status-${status}`}
      className={`inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${map[status] || map.expired}`}
    >
      {status}
    </span>
  );
}
